import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Box,
  Alert,
} from '@mui/material';
import Button from './Button';
import Card from './Card';
import TextField from './TextField';
import {
  FileUpload as FileUploadIcon,
  Code as CodeIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const TestsArrayJsonImport = ({ moduleIndex, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showStructure, setShowStructure] = useState(false);
  const { t } = useTranslation(['courses', 'common']);

  const handleOpen = () => {
    setOpen(true);
    setError('');
    setJsonText('');
    setLoading(false);
  };

  const handleClose = () => {
    setOpen(false);
    setShowStructure(false);
  };

  const handleImport = async () => {
    try {
      if (!jsonText.trim()) {
        setError(t('courses:testsImport.validation.emptyJson'));
        return;
      }
      let parsedData;
      try {
        parsedData = JSON.parse(jsonText);
      } catch (err) {
        setError(t('courses:testsImport.validation.invalidJson') + err.message);
        return;
      }
      // Если данные не являются массивом, но являются объектом, пробуем обработать их как один тест
      if (!Array.isArray(parsedData) && typeof parsedData === 'object') {
        if (parsedData.questions && Array.isArray(parsedData.questions)) {
          parsedData = [parsedData];
        } else if (parsedData.options && Array.isArray(parsedData.options)) {
          parsedData = [parsedData];
        } else {
          setError(t('courses:testsImport.validation.invalidFormat'));
          return;
        }
      }
      const isQuestionImport =
        Array.isArray(parsedData) &&
        parsedData.length > 0 &&
        parsedData.some((item) => 'options' in item && !('questions' in item));
      setLoading(true);
      // --- Импорт всегда только локально, без запросов к серверу ---
      let testsToImport;
      if (isQuestionImport) {
        const questions = parsedData.map((question) => ({
          type: question.type || 'single-choice',
          question:
            question.text ||
            question.question ||
            t('courses:testsImport.defaults.questionNoText'),
          options: question.options || [],
          points: Math.max(1, Number(question.points) || 1),
        }));
        testsToImport = [
          {
            title: t('courses:testsImport.defaults.testTitle'),
            description: t('courses:testsImport.defaults.testDescription'),
            timeLimit: 15,
            passingScore: 70,
            questions: questions,
            _id: `temp-test-${Date.now()}-${Math.random()
              .toString(36)
              .substring(2, 9)}`,
          },
        ];
      } else {
        testsToImport = parsedData.map((test) => ({
          ...test,
          _id: `temp-test-${Date.now()}-${Math.random()
            .toString(36)
            .substring(2, 9)}`,
        }));
      }
      if (onSuccess) {
        onSuccess(moduleIndex, testsToImport);
        toast.success(
          t('courses:testsImport.success.imported', { count: testsToImport.length })
        );
        handleClose();
      }
      setLoading(false);
    } catch (error) {
      setError(t('courses:testsImport.validation.unexpectedError') + error.message);
      setLoading(false);
    }
  };

  const toggleStructure = () => {
    setShowStructure(!showStructure);
  };

  const handleUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        setJsonText(content);
        JSON.parse(content);
        setError('');
      } catch (err) {
        setError(t('courses:testsImport.validation.invalidJson') + err.message);
      }
    };
    reader.readAsText(file);
  };

  // Пример универсальной структуры теста для импорта
  const jsonStructure = [
    {
      title: t('courses:testsImport.example.title'),
      description: t('courses:testsImport.example.description'),
      timeLimit: 15,
      passingScore: 70,
      questions: [
        {
          question: t('courses:testsImport.example.singleChoiceQuestion'),
          type: 'single-choice',
          points: 1,
          options: [
            {
              text: t('courses:testsImport.example.option', { num: 1 }),
              isCorrect: true,
            },
            {
              text: t('courses:testsImport.example.option', { num: 2 }),
              isCorrect: false,
            },
          ],
        },
        {
          question: t('courses:testsImport.example.multipleChoiceQuestion'),
          type: 'multiple-choice',
          points: 2,
          options: [
            {
              text: t('courses:testsImport.example.option', { num: 1 }),
              isCorrect: true,
            },
            {
              text: t('courses:testsImport.example.option', { num: 2 }),
              isCorrect: true,
            },
            {
              text: t('courses:testsImport.example.option', { num: 3 }),
              isCorrect: false,
            },
          ],
        },
        {
          question: t('courses:testsImport.example.trueFalseQuestion'),
          type: 'true-false',
          correctAnswer: 'true', // или 'false'
          points: 1,
        },
        {
          question: t('courses:testsImport.example.textQuestion'),
          type: 'text',
          correctAnswer: t('courses:testsImport.example.correctAnswer'),
          points: 1,
        },
        {
          question: t('courses:testsImport.example.memoryQuestion'),
          type: 'memory',
          points: 1,
          options: [
            {
              text: t('courses:testsImport.example.card', { num: 1 }),
              pairId: 'pair1',
            },
            {
              text: t('courses:testsImport.example.cardPair', { num: 1 }),
              pairId: 'pair1',
            },
            {
              text: t('courses:testsImport.example.card', { num: 2 }),
              pairId: 'pair2',
            },
            {
              text: t('courses:testsImport.example.cardPair', { num: 2 }),
              pairId: 'pair2',
            },
          ],
        },
      ],
    },
  ];

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<CodeIcon />}
        onClick={handleOpen}
        sx={{ ml: 1 }}
        size="small"
      >
        {t('courses:testsImport.button.import')}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={2}>
            <CodeIcon
              color="primary"
              sx={{ fontSize: 28, display: { xs: 'none', md: 'block' } }}
            />
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('courses:testsImport.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('courses:testsImport.subtitle')}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Button
              variant="contained"
              component="label"
              startIcon={<FileUploadIcon />}
              size="medium"
            >
              {t('courses:testsImport.button.upload')}
              <input
                type="file"
                accept=".json"
                hidden
                onChange={handleUpload}
              />
            </Button>
            <Tooltip title={t('courses:testsImport.button.showStructure')}>
              <IconButton onClick={toggleStructure}>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            multiline
            fullWidth
            rows={12}
            value={jsonText}
            onChange={(e) => {
              if (error) setError('');
              setJsonText(e.target.value);
            }}
            variant="outlined"
            error={!!error}
            placeholder={t('courses:testsImport.placeholder')}
            sx={{ mb: 1 }}
          />
          {showStructure && (
            <Card sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('courses:testsImport.structureExample')}
              </Typography>
              <TextField
                multiline
                fullWidth
                rows={10}
                value={JSON.stringify(jsonStructure, null, 2)}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                  },
                }}
              />
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} variant="outlined" size="large">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            size="large"
            disabled={!jsonText.trim() || !!error || loading}
          >
            {loading
              ? t('courses:testsImport.button.importing')
              : t('courses:testsImport.button.importAction')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TestsArrayJsonImport;
